import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Upload, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES_TRAVAUX = [
  "Réparation de toiture",
  "Fuite de toiture / infiltration d'eau",
  "Rénovation de toiture",
  "Réfection de toiture",
  "Entretien, nettoyage et démoussage",
  "Réparation de tuiles",
  "Gouttières et zinguerie",
  "Étanchéité de toiture",
  "Autre / je ne sais pas encore",
];

const inputCls =
  "w-full bg-white border border-midnight/15 px-4 py-3.5 text-base text-midnight placeholder:text-steel focus:border-signal focus:outline-none transition-colors duration-300";

const Field = ({ label, htmlFor, children, optional }) => (
  <div>
    <label htmlFor={htmlFor} className="block font-mono text-[10px] uppercase tracking-[0.2em] text-midnight/60 mb-2">
      {label} {optional && <span className="text-steel normal-case tracking-normal">(facultatif)</span>}
    </label>
    {children}
  </div>
);

export default function DevisForm() {
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", ville: "", type_travaux: "", message: "" });
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (photo) data.append("photo", photo);
      await axios.post(`${API}/devis`, data);
      toast.success("Votre demande de devis a bien été envoyée. MSC Toitures vous recontacte rapidement.");
      setForm({ nom: "", telephone: "", email: "", ville: "", type_travaux: "", message: "" });
      setPhoto(null);
      e.target.reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue. Vous pouvez nous appeler directement.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <p className="text-lg text-midnight/70 leading-relaxed">
          Décrivez votre projet en quelques lignes : nous vous rappelons pour convenir d'une visite.{" "}
          <strong className="text-midnight">Le déplacement et le devis sont gratuits</strong>, partout dans le
          Val-de-Marne.
        </p>
        <div className="mt-10 space-y-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-midnight/50">Ou appelez-nous directement</p>
          <a
            href="tel:0646300391"
            data-testid="devis-phone-1"
            className="flex items-center gap-4 border border-midnight/15 bg-white px-5 py-4 hover:border-signal transition-colors duration-300 group"
          >
            <Phone size={18} strokeWidth={1.5} className="text-signal" />
            <span className="font-display font-extrabold text-2xl tracking-tight group-hover:text-signal transition-colors duration-300">
              06 46 30 03 91
            </span>
          </a>
          <a
            href="tel:0187078662"
            data-testid="devis-phone-2"
            className="flex items-center gap-4 border border-midnight/15 bg-white px-5 py-4 hover:border-signal transition-colors duration-300 group"
          >
            <Phone size={18} strokeWidth={1.5} className="text-signal" />
            <span className="font-display font-extrabold text-2xl tracking-tight group-hover:text-signal transition-colors duration-300">
              01 87 07 86 62
            </span>
          </a>
        </div>
      </div>
      <form onSubmit={submit} className="lg:col-span-7 space-y-6" data-testid="devis-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Nom" htmlFor="devis-nom">
            <input id="devis-nom" data-testid="devis-nom-input" required autoComplete="name" value={form.nom} onChange={update("nom")} className={inputCls} placeholder="Votre nom" />
          </Field>
          <Field label="Téléphone" htmlFor="devis-tel">
            <input id="devis-tel" data-testid="devis-telephone-input" required type="tel" inputMode="tel" autoComplete="tel" value={form.telephone} onChange={update("telephone")} className={inputCls} placeholder="06 00 00 00 00" />
          </Field>
          <Field label="Email" htmlFor="devis-email">
            <input id="devis-email" data-testid="devis-email-input" required type="email" inputMode="email" autoComplete="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="vous@exemple.fr" />
          </Field>
          <Field label="Ville" htmlFor="devis-ville">
            <input id="devis-ville" data-testid="devis-ville-input" required autoComplete="address-level2" value={form.ville} onChange={update("ville")} className={inputCls} placeholder="Champigny-sur-Marne" />
          </Field>
        </div>
        <Field label="Type de travaux" htmlFor="devis-type">
          <select id="devis-type" data-testid="devis-type-select" required value={form.type_travaux} onChange={update("type_travaux")} className={inputCls}>
            <option value="" disabled>Sélectionnez le type de travaux</option>
            {TYPES_TRAVAUX.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Message" htmlFor="devis-message" optional>
          <textarea id="devis-message" data-testid="devis-message-textarea" rows={4} value={form.message} onChange={update("message")} className={inputCls} placeholder="Décrivez votre toiture, la fuite constatée, le problème rencontré…" />
        </Field>
        <Field label="Photo de votre toiture" htmlFor="devis-photo" optional>
          <label
            htmlFor="devis-photo"
            className="flex items-center gap-3 border border-dashed border-steel/50 bg-white px-4 py-4 cursor-pointer hover:border-signal transition-colors duration-300"
          >
            <Upload size={16} strokeWidth={1.5} className="text-signal shrink-0" />
            <span className="text-sm text-midnight/60">
              {photo ? photo.name : "Joindre une photo (tuile cassée, trace d'infiltration…)"}
            </span>
          </label>
          <input id="devis-photo" data-testid="devis-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
        </Field>
        <button
          type="submit"
          disabled={sending}
          data-testid="devis-submit-button"
          className="btn-sweep w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-signal text-white font-display font-bold text-base uppercase tracking-wide px-10 py-4 disabled:opacity-60 transition-colors duration-300"
        >
          {sending && <Loader2 size={18} className="animate-spin" />}
          {sending ? "Envoi en cours…" : "Demander mon devis gratuit"}
        </button>
      </form>
    </div>
  );
}
